import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Bell } from "lucide-react";
import { api } from "@/lib/api";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

function fmtWhen(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const now = new Date();
    const same = d.toDateString() === now.toDateString();
    if (same) return d.toLocaleTimeString("ru-RU", { hour: "2-digit", minute: "2-digit" });
    return d.toLocaleDateString("ru-RU", { day: "numeric", month: "short" });
  } catch { return ""; }
}

/**
 * Compact bell + panel of internal notifications.
 * Polls unread count periodically. Clicking a notification marks it read and deep-links.
 */
export default function NotificationBell({ testid = "notification-bell", pollMs = 30000 }) {
  const nav = useNavigate();
  const [count, setCount] = useState(0);
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const loadCount = useCallback(async () => {
    try {
      const { data } = await api.get("/account/notifications/unread-count");
      setCount(data.count || 0);
    } catch { /* noop */ }
  }, []);

  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/account/notifications");
      setItems(data || []);
    } catch { /* noop */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    loadCount();
    const t = setInterval(loadCount, pollMs);
    return () => clearInterval(t);
  }, [loadCount, pollMs]);

  useEffect(() => {
    if (open) loadList();
  }, [open, loadList]);

  const onItemClick = async (n) => {
    try {
      if (!n.read) await api.post(`/account/notifications/${n.id}/read`);
    } catch { /* noop */ }
    setOpen(false);
    setItems((xs) => xs.map((x) => (x.id === n.id ? { ...x, read: true } : x)));
    setCount((c) => (n.read ? c : Math.max(0, c - 1)));
    if (n.deep_link) nav(n.deep_link);
  };

  const markAll = async () => {
    try {
      await api.post("/account/notifications/read-all");
      setItems((xs) => xs.map((x) => ({ ...x, read: true })));
      setCount(0);
    } catch { /* noop */ }
  };

  const badge = count > 0;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={testid}
          aria-label={badge ? `Уведомления: ${count} непрочитанных` : "Уведомления"}
          className="relative shrink-0 inline-flex items-center justify-center h-9 w-9 rounded-full border border-espresso/25 bg-white/70 hover:bg-white text-espresso"
        >
          <Bell className="w-4 h-4" strokeWidth={1.6} />
          {badge && (
            <span
              data-testid={`${testid}-badge`}
              className="absolute -top-0.5 -right-0.5 min-w-[16px] h-[16px] px-1 rounded-full bg-[#B84A2A] text-white text-[10px] font-medium flex items-center justify-center leading-none"
            >
              {count > 9 ? "9+" : count}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        sideOffset={8}
        className="w-[320px] sm:w-[360px] rounded-2xl p-0 overflow-hidden"
        data-testid={`${testid}-panel`}
      >
        <div className="flex items-center justify-between px-4 py-2.5 border-b hairline bg-cream/70">
          <div className="text-xs uppercase tracking-widest text-espresso">Уведомления</div>
          {items.some((n) => !n.read) && (
            <button
              type="button"
              onClick={markAll}
              className="text-[11px] uppercase tracking-widest text-espresso/70 hover:text-espresso"
              data-testid={`${testid}-mark-all`}
            >
              Прочитать всё
            </button>
          )}
        </div>
        <div className="max-h-[380px] overflow-y-auto">
          {loading ? (
            <div className="px-4 py-6 text-center text-xs text-muted-foreground font-serif italic">Загрузка…</div>
          ) : items.length === 0 ? (
            <div className="px-4 py-6 text-center text-sm text-muted-foreground font-serif italic" data-testid={`${testid}-empty`}>
              Уведомлений пока нет
            </div>
          ) : (
            <ul>
              {items.map((n) => (
                <li key={n.id}>
                  <button
                    type="button"
                    onClick={() => onItemClick(n)}
                    data-testid={`${testid}-item-${n.id}`}
                    className={`w-full text-left px-4 py-3 hover:bg-sand/30 border-b hairline last:border-b-0 ${!n.read ? "bg-sand/20" : ""}`}
                  >
                    <div className="flex gap-2 items-start">
                      {!n.read && <span className="mt-1.5 shrink-0 w-2 h-2 rounded-full bg-[#B84A2A]" />}
                      <div className="min-w-0 flex-1">
                        <div className="text-sm text-espresso leading-snug">{n.text}</div>
                        {n.action_label && <div className="mt-2 inline-flex h-8 items-center rounded-md px-3 text-[12px] bg-espresso text-white" data-testid={`${testid}-action-${n.id}`}>{n.action_label}</div>}\n                        <div className="text-[11px] text-muted-foreground mt-1">{fmtWhen(n.created_at)}</div>
                      </div>
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

